import { Component, computed, inject, LOCALE_ID, signal } from "@angular/core";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { GC_PER_AUD } from "@bitwarden/web-vault/app/models/constants/credits.constants";
import { GLOBAL_BASE_CURRENCY } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { formatCurrencyAmount } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";

// The redeem action below is a UI-only mock — the Amazon Gift Card redemption backend is
// still being built. The exchange rate itself (GC_PER_AUD) is real and shared with the
// "Buy with Cash" header badge, not part of the mock.
const MIN_AMOUNT_AUD = 5;
const DENOMINATIONS_AUD = [5, 10, 25, 50] as const;

@Component({
  selector: "app-amazon-gift-card",
  templateUrl: "./amazon-gift-card.component.html",
})
export class AmazonGiftCardComponent {
  private notifyService = inject(ConfirmationDialogService);
  private localeId = inject(LOCALE_ID);

  protected readonly gcPerAud = GC_PER_AUD;
  protected readonly minAmount = MIN_AMOUNT_AUD;
  protected readonly denominations = DENOMINATIONS_AUD;

  protected selectedAmount = signal<number>(DENOMINATIONS_AUD[2]);
  protected isCustom = signal(false);

  protected readonly requiredPoints = computed(() => this.selectedAmount() * this.gcPerAud);
  protected readonly isRedeemable = computed(() => this.selectedAmount() >= this.minAmount);

  protected readonly formattedRate = computed(() => this.gcPerAud.toLocaleString());
  protected readonly formattedAmount = computed(() =>
    formatCurrencyAmount(this.selectedAmount(), GLOBAL_BASE_CURRENCY, this.localeId),
  );
  protected readonly formattedPoints = computed(() => this.requiredPoints().toLocaleString());

  selectDenomination(amount: number): void {
    this.isCustom.set(false);
    this.selectedAmount.set(amount);
  }

  selectCustom(event: FocusEvent): void {
    // The custom input is uncontrolled (no [value] binding), so on refocus it may still hold
    // digits the user typed before switching to a denomination pill. Read the DOM value back
    // rather than forcing 0, otherwise the hint/Redeem button would act on a stale 0 while the
    // box visibly still shows what was typed.
    this.isCustom.set(true);
    const parsed = Number((event.target as HTMLInputElement).value);
    this.selectedAmount.set(Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
  }

  onCustomAmountChange(event: Event): void {
    const parsed = Number((event.target as HTMLInputElement).value);
    this.selectedAmount.set(Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
  }

  redeem(): void {
    // TODO: wire to the real Amazon Gift Card redemption API once it's available.
    this.notifyService.notify(NotificationEnum.info, "comingSoon", "amazonGiftCardComingSoonDesc");
  }
}
